import { useState, useEffect } from 'react';
import DynamicMenus from './DynamicMenus';

const Layout = ({ children }) => {
    //Dashboard 1
    // Sidebar and mobile menu state
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [activeMenu, setActiveMenu] = useState(null);
    const [currentPage, setCurrentPage] = useState('dashboard');

    // CRUD Form state
    const [formData, setFormData] = useState({
        id: '',
        name: '',
        email: '',
        role: '',
        status: 'active'
    });
    const [items, setItems] = useState([
        { id: '1', name: 'John Doe', email: 'john@example.com', role: 'Admin', status: 'active' },
        { id: '2', name: 'Jane Smith', email: 'jane@example.com', role: 'User', status: 'active' }
    ]);
    const [editingId, setEditingId] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');

    // Mobile responsiveness
    useEffect(() => {
        const handleResize = () => {
            if (window.innerWidth >= 768) {
                setSidebarOpen(false);
            }
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Menu items configuration
    const menuItems = [
        { id: 'dashboard', label: 'Dashboard', icon: '📊', page: 'dashboard' },
        {
            id: 'users',
            label: 'User Management',
            icon: '👥',
            subItems: [
                { id: 'users-list', label: 'All Users', page: 'users-list' },
                { id: 'users-add', label: 'Add User', page: 'users-add' },
                { id: 'users-roles', label: 'User Roles', page: 'users-roles' }
            ]
        },
        {
            id: 'products',
            label: 'Products',
            icon: '📦',
            subItems: [
                { id: 'products-list', label: 'Product List', page: 'products-list' },
                { id: 'products-add', label: 'Add Product', page: 'products-add' },
                { id: 'products-categories', label: 'Categories', page: 'products-categories' }
            ]
        },
        {
            id: 'orders',
            label: 'Orders',
            icon: '🛒',
            subItems: [
                { id: 'orders-all', label: 'All Orders', page: 'orders-all' },
                { id: 'orders-pending', label: 'Pending Orders', page: 'orders-pending' },
                { id: 'orders-completed', label: 'Completed Orders', page: 'orders-completed' }
            ]
        },
        { id: 'analytics', label: 'Analytics', icon: '📈', page: 'analytics' },
        { id: 'settings', label: 'Settings', icon: '⚙️', page: 'settings' },
        { id: 'support', label: 'Support', icon: '💬', page: 'support' }
    ];

    const toggleMenu = (menuId) => {
        setActiveMenu(activeMenu === menuId ? null : menuId);
    };

    const handleNavClick = (page) => {
        setCurrentPage(page);
        setSidebarOpen(false);
    };

    // CRUD Operations
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const resetForm = () => {
        setFormData({ id: '', name: '', email: '', role: '', status: 'active' });
        setEditingId(null);
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!formData.name || !formData.email || !formData.role) return;

        if (editingId) {
            setItems(items.map(item =>
                item.id === editingId
                    ? { ...formData, id: editingId }
                    : item
            ));
        } else {
            const newItem = {
                ...formData,
                id: Date.now().toString()
            };
            setItems([...items, newItem]);
        }
        resetForm();
    };

    const handleEdit = (item) => {
        setFormData(item);
        setEditingId(item.id);
    };

    const handleDelete = (id) => {
        if (window.confirm('Are you sure you want to delete this item?')) {
            setItems(items.filter(item => item.id !== id));
            if (editingId === id) resetForm();
        }
    };

    const filteredItems = items.filter(item =>
        item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.role.toLowerCase().includes(searchTerm.toLowerCase())
    );


    useEffect(() => {
        const checkAuthAndActivity = () => {
            const token = localStorage.getItem('tcn');
            const lastActivityTime = localStorage.getItem('lastActivityTime');
            const currentTime = Date.now();
            const thirtyMinutesInMs = 30 * 60 * 1000;

            // Check if token exists
            if (!token) {
                localStorage.clear();
                window.location.href = '/login';
                return;
            }

            // Check if app has been inactive for more than 30 minutes
            if (lastActivityTime) {
                const timeDifference = currentTime - parseInt(lastActivityTime);
                if (timeDifference > thirtyMinutesInMs) {
                    localStorage.clear();
                    window.location.href = '/login';
                    return;
                }
            }

            // Update last activity time
            localStorage.setItem('lastActivityTime', currentTime.toString());
        };

        // Check on component mount
        checkAuthAndActivity();

        // Set up activity listener
        const activityEvents = ['mousedown', 'keydown', 'scroll', 'touchstart'];
        const handleActivity = () => {
            localStorage.setItem('lastActivityTime', Date.now().toString());
        };

        activityEvents.forEach(event => {
            window.addEventListener(event, handleActivity);
        });

        // Check periodically
        const interval = setInterval(checkAuthAndActivity, 60000); // Check every minute

        return () => {
            activityEvents.forEach(event => {
                window.removeEventListener(event, handleActivity);
            });
            clearInterval(interval);
        };
    }, []);

    const handleLogout = () => {
        localStorage.clear();
        window.location.href = '/login';
    };


    return (
        <div className="flex h-screen bg-gray-50 overflow-hidden">
            {/* Mobile sidebar overlay */}
            {sidebarOpen && (
                <div
                    className="fixed inset-0 z-40 bg-gray-600 bg-opacity-75 transition-opacity md:hidden"
                    onClick={() => setSidebarOpen(false)}
                />
            )}

            {/* Sidebar */}
            <div className={`fixed inset-y-0 left-0 z-50 w-64 bg-white shadow-lg transform transition-transform duration-300 ease-in-out md:translate-x-0 md:static md:inset-0 flex flex-col overflow-hidden ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'
                }`}>

                {/* Sidebar Header */}
                <div className="flex items-center justify-between h-16 px-4 bg-gradient-to-r from-indigo-600 to-purple-600">
                    <div className="flex items-center space-x-2">
                        <div className="w-8 h-8 bg-white rounded-lg flex items-center justify-center">
                            <span className="text-indigo-600 font-bold text-lg">A</span>
                        </div>
                        <h1 className="text-white font-bold text-lg hidden sm:block">AskMeReality</h1>
                    </div>
                    <button
                        onClick={() => setSidebarOpen(false)}
                        className="text-white hover:text-gray-200 md:hidden focus:outline-none"
                    >
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                {/* Navigation */}

                <DynamicMenus setCurrentPage={setCurrentPage} />

                {/* <nav className="flex-1 px-4 py-6 space-y-2 overflow-y-auto">
                    {menuItems.map((item) => (
                        <div key={item.id}>
                            {item.subItems ? (
                                <>
                                    <button
                                        onClick={() => toggleMenu(item.id)}
                                        className="w-full flex items-center justify-between px-3 py-2.5 text-left text-gray-700 hover:bg-indigo-50 hover:text-indigo-700 rounded-lg transition-all duration-200 group"
                                    >
                                        <div className="flex items-center space-x-3">
                                            <span className="text-xl">{item.icon}</span>
                                            <span className="font-medium">{item.label}</span>
                                        </div>
                                        <svg
                                            className={`w-4 h-4 transition-transform duration-200 ${activeMenu === item.id ? 'rotate-90' : ''
                                                }`}
                                            fill="none"
                                            stroke="currentColor"
                                            viewBox="0 0 24 24"
                                        >
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                                        </svg>
                                    </button>


                                    <div className={`mt-2 ml-6 space-y-1 transition-all duration-300 ${activeMenu === item.id ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0 overflow-hidden'
                                        }`}>
                                        {item.subItems.map((subItem) => (
                                            <button
                                                key={subItem.id}
                                                onClick={() => handleNavClick(subItem.page)}
                                                className={`w-full px-3 py-2 text-left text-sm rounded-lg transition-all duration-200 ${currentPage === subItem.page
                                                    ? 'bg-indigo-100 text-indigo-700 font-medium'
                                                    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                                                    }`}
                                            >
                                                {subItem.label}
                                            </button>
                                        ))}
                                    </div>
                                </>
                            ) : (
                                <button
                                    onClick={() => handleNavClick(item.page)}
                                    className={`w-full flex items-center space-x-3 px-3 py-2.5 text-left rounded-lg transition-all duration-200 ${currentPage === item.page
                                        ? 'bg-indigo-100 text-indigo-700 font-medium'
                                        : 'text-gray-700 hover:bg-indigo-50 hover:text-indigo-700'
                                        }`}
                                >
                                    <span className="text-xl">{item.icon}</span>
                                    <span className="font-medium">{item.label}</span>
                                </button>
                            )}
                        </div>
                    ))}
                </nav> */}

                {/* Sidebar Footer */}
                <div className="p-4 border-t border-gray-200">
                    <div className="flex items-center space-x-3 p-2 rounded-lg bg-gray-50">
                        <div className="w-8 h-8 bg-gradient-to-r from-indigo-500 to-purple-500 rounded-full flex items-center justify-center">
                            <span className="text-white font-medium text-sm">A</span>
                        </div>
                        <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900 truncate">Amit Malikpuria</p>
                            <p className="text-xs text-gray-500 truncate">Admin</p>
                        </div>
                    </div>
                </div>

            </div>

            {/* Main Content */}
            <div className="flex-1 flex flex-col overflow-hidden">

                <header className="bg-white shadow-sm border-b border-gray-200 px-4 sm:px-6 lg:px-8">
                    <div className="flex justify-between items-center h-16">
                        <div className="flex items-center space-x-4">
                            <button
                                onClick={() => setSidebarOpen(true)}
                                className="text-gray-500 hover:text-gray-700 md:hidden focus:outline-none focus:ring-2 focus:ring-indigo-500 p-2 rounded-lg"
                            >
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
                                </svg>
                            </button>
                            <h1 className="text-xl sm:text-2xl font-bold text-gray-900 capitalize">
                                {currentPage.replace('-', ' ')}
                            </h1>
                        </div>

                        <div className="flex items-center space-x-4">

                            <div className="relative hidden sm:block">
                                <input
                                    type="text"
                                    placeholder="Search..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="w-64 pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                                />
                                <svg className="w-5 h-5 text-gray-400 absolute left-3 top-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                </svg>
                            </div>


                            <button className="relative p-2 text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 rounded-lg">
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                                </svg>
                                <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full"></span>
                            </button>
                            <button
                                className="w-full flex items-center justify-between px-3 py-2.5 text-left text-white bg-gradient-to-r from-red-500 to-red-700 hover:from-red-600 hover:to-red-800 rounded-lg transition-all duration-200 group"
                                onClick={() => handleLogout()}
                            >
                                <div className="flex items-center space-x-3">
                                    <span className="font-medium text-center">Logout</span>
                                </div>
                            </button>
                        </div>
                    </div>
                </header>
                {children}
            </div>
        </div>
    );
};

export default Layout;
